import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../utils/Api';
import { api_url_v1 } from '../utils/Config';

const initialState = {
  items: [],
  selectedFacility: null,
  meta: {
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0
  },
  loading: false,
  error: null
};

export const fetchHealthFacilities = createAsyncThunk(
  'healthFacilities/fetchHealthFacilities',
  async ({ page, limit, activeFilters, search }, { getState }) => {
    const { auth: { token } } = getState();
    const processedParams = {};
    for (const key in activeFilters) {
      processedParams[key] = Array.isArray(activeFilters[key]) 
        ? activeFilters[key].join(',') 
        : activeFilters[key];
    }
    const params = {
      page,
      limit,
    //   [`filter[${filter_type}]`]: `${filter}`,
      search,
      ...processedParams
    }
    const response = await api.get(`${api_url_v1}/health-facilities`, {
      params,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      }
    });
    
    return {
      data: response.data.data || [],
      meta: response.data.meta || {
        current_page: page,
        last_page: 1,
        per_page: limit,
        total: 0
      }
    };
  }
);

export const createHealthFacility = createAsyncThunk(
    'healthFacilities/createHealthFacility',
    async (facilityData, { getState, rejectWithValue }) => {
      try {
        const { auth: { token } } = getState();
        const response = await api.post(`${api_url_v1}/health-facilities`, facilityData, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        });
        return response.data.data;
      } catch (error) {
        return rejectWithValue(error.response?.data || 'Facility creation failed');
      }
    }
);

export const fetchHealthFacilityDetails = createAsyncThunk(
  'healthFacilities/fetchHealthFacilityDetails',
  async (id, { getState }) => {
    const { auth: { token } } = getState();
    const response = await api.get(`${api_url_v1}/health-facilities/${id}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      }
    });
    return response.data;
  }
);

export const updateHealthFacility = createAsyncThunk(
  'healthFacilities/updateHealthFacility',
  async ({ id, data }, { getState, rejectWithValue }) => {
    try {
      const { auth: { token } } = getState();
      const response = await api.put(`${api_url_v1}/health-facilities/${id}`, data, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
      return { id, updatedFacility: response.data.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Update failed');
    }
  }
);
export const deleteHealthFacility = createAsyncThunk(
    'healthFacilities/deleteHealthFacility',
    async (id, { getState, rejectWithValue }) => {
      try {
        const { auth: { token } } = getState();
        await api.delete(`${api_url_v1}/health-facilities/${id}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        });
        return id;
      } catch (error) {
        return rejectWithValue(error.response?.data || 'Delete failed');
      }
    }
  );

  export const importHealthFacilities = createAsyncThunk(
    'healthFacilities/importHealthFacilities',
    async (file, { getState, rejectWithValue }) => {
      try {
        const { auth: { token } } = getState();
        
        // Create FormData object
        const formData = new FormData();
        formData.append('file', file);
  
        const response = await api.post(`${api_url_v1}/health-facilities/import`, formData, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data', // Important for file upload
          }
        });
        
        return response.data;
      } catch (error) {
        return rejectWithValue(error.response?.data || 'Import failed');
      }
    }
  );

const healthFacilitiesSlice = createSlice({
  name: 'healthFacilitiesSlice',
  initialState,
  reducers: {
    setSelectedFacility: (state, action) => {
      state.selectedFacility = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch Facilities Cases
      .addCase(fetchHealthFacilities.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchHealthFacilities.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.data;
        state.meta = action.payload.meta;
      })
      .addCase(fetchHealthFacilities.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      
      // Fetch Facility Details Cases
      .addCase(fetchHealthFacilityDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchHealthFacilityDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedFacility = action.payload.data;
      })
      .addCase(fetchHealthFacilityDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      
      // Update Facility Cases
      .addCase(updateHealthFacility.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateHealthFacility.fulfilled, (state, action) => {
        state.loading = false;
        
        // Update the facility in the items array
        const index = state.items.findIndex(facility => facility.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload.updatedFacility;
        }
        
        // Update selectedFacility if it's the same facility
        if (state.selectedFacility?.id === action.payload.id) {
          state.selectedFacility = action.payload.updatedFacility;
        }
      })
      .addCase(updateHealthFacility.rejected, (state, action) => {
        state.loading = true;
        state.error = action.payload || 'Update failed';
      })
      .addCase(createHealthFacility.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createHealthFacility.fulfilled, (state, action) => {
        state.loading = false;
        // Add the new facility to the items array
        state.items.push(action.payload);
      })
      .addCase(createHealthFacility.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Facility creation failed';
      })
      .addCase(deleteHealthFacility.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteHealthFacility.fulfilled, (state, action) => {
        state.loading = false;
        // Remove the deleted facility from the items array
        state.items = state.items.filter(facility => facility.id !== action.payload);
        // Clear selectedFacility if it was the deleted facility
        if (state.selectedFacility?.id === action.payload) {
          state.selectedFacility = null;
        }
        // Update the total count in meta
        state.meta.total = Math.max(0, state.meta.total - 1);
      })
      .addCase(deleteHealthFacility.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Delete failed';
      })
      .addCase(importHealthFacilities.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(importHealthFacilities.fulfilled, (state, action) => {
        state.loading = false;
        // Optionally refresh the facilities list or handle the response
        // You might want to dispatch fetchHealthFacilities here
      })
      .addCase(importHealthFacilities.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Import failed';
      })
      ;
  }
});

export const { setSelectedFacility } = healthFacilitiesSlice.actions;
export default healthFacilitiesSlice.reducer;